/**
 * Plan context — the well + event + plan version the authoring screens point
 * at. W0 sets well/event when a plan is opened or created; W1–W9 read this to
 * know which plan version to load and edit. Selecting a new well clears the
 * event and plan; selecting a new event clears the plan, so context never
 * leaks across wells or events.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface WellContextState {
  selectedWellId: string | null;
  /** Active event id; null means "let the screens pick a sensible default". */
  activeEventId: string | null;
  /** Active plan version id being authored; null until a plan is opened/created. */
  activePlanId: string | null;
}

const initialState: WellContextState = {
  selectedWellId: null,
  activeEventId: null,
  activePlanId: null,
};

const wellContextSlice = createSlice({
  name: "wellContext",
  initialState,
  reducers: {
    selectWell(state, action: PayloadAction<string | null>) {
      if (state.selectedWellId !== action.payload) {
        state.activeEventId = null;
        state.activePlanId = null;
      }
      state.selectedWellId = action.payload;
    },
    setActiveEvent(state, action: PayloadAction<string | null>) {
      if (state.activeEventId !== action.payload) {
        state.activePlanId = null;
      }
      state.activeEventId = action.payload;
    },
    setActivePlan(state, action: PayloadAction<string | null>) {
      state.activePlanId = action.payload;
    },
  },
});

export const { selectWell, setActiveEvent, setActivePlan } = wellContextSlice.actions;
export const wellContextReducer = wellContextSlice.reducer;
